import { useRef, useState } from "react";
import {
    ArrowRight,
    CalendarDays,
    Eye,
    EyeOff,
    LockKeyhole,
    ShieldCheck,
    Sparkles,
    Target,
    UsersRound,
} from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { accessCodeFor, formatDobInput } from "@/auth/accessCode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const publicUrl = process.env.PUBLIC_URL || "";

const SaiMark = () => (
    <img
        src={`${publicUrl}/images/sssso-logo.png`}
        alt="Sri Sathya Sai Seva Organisations"
        className="h-14 w-14 shrink-0 object-contain"
    />
);

export const LoginPage = () => {
    const { login } = useAuth();
    const [saiConnectId, setSaiConnectId] = useState("");
    const [dobParts, setDobParts] = useState({ day: "", month: "", year: "" });
    const [showSaiConnectId, setShowSaiConnectId] = useState(false);
    const [showDob, setShowDob] = useState(false);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (event) => {
        event.preventDefault();
        setError("");

        const dateOfBirth = `${dobParts.day}-${dobParts.month}-${dobParts.year}`;
        const accessCode = accessCodeFor(saiConnectId, dateOfBirth);
        if (!saiConnectId.trim() || !dobParts.day || !dobParts.month || !dobParts.year) {
            setError("Please enter your SaiConnect ID and Date of Birth.");
            return;
        }
        if (!accessCode) {
            setError("Please enter a valid Date of Birth.");
            return;
        }

        setSubmitting(true);
        try {
            await login(accessCode);
            setSaiConnectId("");
            setDobParts({ day: "", month: "", year: "" });
        } catch (requestError) {
            setError(requestError.message || "Unable to verify access.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex h-dvh flex-col overflow-hidden bg-background">
            <header className="shrink-0 border-b border-border/70 bg-card/95 backdrop-blur">
                <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2 sm:px-6 md:py-3 lg:px-8">
                    <div className="flex items-center gap-3">
                        <SaiMark />
                        <div>
                            <p className="font-serif-elegant text-xl leading-tight text-foreground">
                                Sri Sathya Sai Seva Organisations
                            </p>
                            <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                                Sai Connect Dashboard
                            </p>
                        </div>
                    </div>
                    <span className="hidden rounded-full border border-primary/20 bg-primary/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.13em] text-primary sm:inline-flex">
                        Secure organisational access
                    </span>
                </div>
            </header>

            <main className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col px-3 py-2 sm:px-5 md:py-3 lg:px-8">
                <section className="relative min-h-0 flex-1 overflow-hidden rounded-[24px] bg-gradient-to-br from-slate-950 via-slate-900 to-primary/80 shadow-soft md:rounded-[30px]">
                    <div
                        className="pointer-events-none absolute inset-y-0 left-0 hidden w-1/2 bg-contain bg-left-bottom bg-no-repeat opacity-[0.13] mix-blend-screen md:block"
                        style={{ backgroundImage: `url(${publicUrl}/images/swami-silhouette.png)` }}
                    />
                    <div className="pointer-events-none absolute -left-28 top-[-12rem] hidden h-[30rem] w-[30rem] rounded-full bg-white/5 blur-3xl lg:block" />
                    <div className="pointer-events-none absolute bottom-[-12rem] right-[30%] h-[26rem] w-[26rem] rounded-full bg-primary/20 blur-3xl" />

                    <div className="relative grid h-full min-h-0 md:grid-cols-[1.05fr_0.95fr]">
                        <div className="relative z-10 hidden min-h-0 flex-col justify-between px-6 py-5 text-white md:flex lg:px-9 lg:py-7 xl:px-12">
                            <div>
                                <div className="flex items-center gap-3">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/10 backdrop-blur">
                                        <Sparkles className="h-5 w-5 text-white/90" strokeWidth={1.7} />
                                    </div>
                                    <div>
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/55">
                                            Sai Connect Dashboard
                                        </p>
                                        <p className="mt-0.5 text-sm font-semibold text-white/90">
                                            The <span className="italic text-primary-glow">Insight</span>
                                            {" behind "}
                                            <span className="italic text-primary-glow">Plain Sight</span>
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-5 max-w-xl lg:mt-8 xl:mt-12">
                                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/55">
                                        Welcome to Sai Connect
                                    </p>
                                    <h1 className="mt-3 font-serif-elegant text-3xl leading-[1.12] text-white lg:text-4xl xl:text-[3.15rem]">
                                        <span className="block">Discover Potential.</span>
                                        <span className="mt-[clamp(0.4rem,1.4vh,0.85rem)] block">
                                            Strengthen Purpose.
                                        </span>
                                        <span className="mt-[clamp(0.4rem,1.4vh,0.85rem)] block text-white/70">
                                            Enable Transformation.
                                        </span>
                                    </h1>
                                    <div className="mt-4 h-px w-20 bg-white/30 xl:mt-6" />
                                    <p className="mt-4 max-w-lg text-xs leading-5 text-white/70 lg:text-sm xl:mt-6 xl:leading-6">
                                        More than a report - a shared view of where we are, where attention is needed,
                                        and how every role can contribute to a stronger Organisation.
                                    </p>
                                </div>
                            </div>

                            <div className="mt-5 grid grid-cols-3 gap-2 lg:gap-3">
                                <PurposeCard icon={Target} title="Focus" text="See what needs attention." />
                                <PurposeCard
                                    icon={UsersRound}
                                    title="Strengthen"
                                    text="Enable Samithis and Bhajan Mandalis with insight."
                                />
                                <PurposeCard
                                    icon={ArrowRight}
                                    title="Act"
                                    text="Turn insight into inspiration."
                                />
                            </div>
                        </div>

                        <div className="relative z-20 m-2 flex min-h-0 items-center rounded-[22px] bg-card sm:m-3 md:ml-0 md:rounded-[26px] lg:m-4 lg:ml-0">
                            <div className="w-full px-5 py-4 sm:px-8 md:px-6 lg:px-9 xl:px-12">
                                <div className="mx-auto max-w-md">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                                        <LockKeyhole className="h-5 w-5" strokeWidth={1.8} />
                                    </div>
                                    <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
                                        Authorised access
                                    </p>
                                    <h2 className="mt-1 font-serif-elegant text-2xl leading-tight text-foreground lg:text-3xl">
                                        Sign in to continue
                                    </h2>
                                    <p className="mt-2 text-xs leading-5 text-muted-foreground lg:text-sm">
                                        Use your SaiConnect ID and Date of Birth to access the dashboard authorised for
                                        your role.
                                    </p>

                                    <form onSubmit={submit} className="mt-4 space-y-3 lg:mt-5" noValidate>
                                        <div>
                                            <Label htmlFor="saiConnectId" className="text-sm font-semibold text-foreground">
                                                SaiConnect ID
                                            </Label>
                                            <div className="relative mt-2">
                                                <UsersRound className="pointer-events-none absolute left-4 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                <Input
                                                    id="saiConnectId"
                                                    name="saiConnectId"
                                                    type={showSaiConnectId ? "text" : "password"}
                                                    autoComplete="off"
                                                    autoCapitalize="none"
                                                    spellCheck="false"
                                                    value={saiConnectId}
                                                    onChange={(event) => setSaiConnectId(event.target.value)}
                                                    placeholder="Enter your SaiConnect ID"
                                                    className="h-11 rounded-xl bg-background px-11 py-2 pr-12 text-sm shadow-none focus-visible:border-primary/50 focus-visible:ring-4 focus-visible:ring-primary/10"
                                                    aria-invalid={Boolean(error)}
                                                    aria-describedby={error ? "login-error" : undefined}
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => setShowSaiConnectId((value) => !value)}
                                                    className="absolute right-2 top-1/2 h-8 w-8 -translate-y-1/2 rounded-lg text-muted-foreground hover:text-foreground"
                                                    aria-label={showSaiConnectId ? "Hide SaiConnect ID" : "Show SaiConnect ID"}
                                                    title={showSaiConnectId ? "Hide SaiConnect ID" : "Show SaiConnect ID"}
                                                >
                                                    {showSaiConnectId ? <EyeOff /> : <Eye />}
                                                </Button>
                                            </div>
                                        </div>

                                        <div>
                                            <Label htmlFor="dateOfBirth" className="text-sm font-semibold text-foreground">
                                                Date of Birth
                                            </Label>
                                            <DobInput
                                                parts={dobParts}
                                                onChange={setDobParts}
                                                visible={showDob}
                                                onToggleVisibility={() => setShowDob((value) => !value)}
                                                invalid={Boolean(error)}
                                            />
                                        </div>

                                        {error ? (
                                            <div
                                                id="login-error"
                                                className="rounded-xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive"
                                                role="alert"
                                            >
                                                {error}
                                            </div>
                                        ) : null}

                                        <Button
                                            type="submit"
                                            disabled={submitting}
                                            className="group h-11 w-full rounded-xl px-5 py-2 text-sm font-semibold transition hover:-translate-y-0.5 hover:shadow-md disabled:hover:translate-y-0"
                                        >
                                            <LockKeyhole strokeWidth={1.8} />
                                            <span>{submitting ? "Signing in..." : "Sign In"}</span>
                                            {!submitting ? (
                                                <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
                                            ) : null}
                                        </Button>
                                    </form>

                                    <div className="mt-3 flex items-center justify-center gap-2 border-t border-border/70 pt-2 text-center text-[9px] leading-4 text-muted-foreground lg:text-[10px]">
                                        <ShieldCheck className="h-4 w-4 text-primary" strokeWidth={1.8} />
                                        <span>
                                            Use this dashboard to understand, prioritise and strengthen the organisation
                                            within your jurisdiction.
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <div className="mt-1.5 shrink-0 px-2 text-center text-[9px] tracking-[0.05em] text-muted-foreground sm:text-[10px]">
                    <span>Copyright © 2026 Sri Sathya Sai Seva Organisations. All rights reserved.</span>
                </div>
            </main>
        </div>
    );
};

const PurposeCard = ({ icon: Icon, title, text, className = "" }) => (
    <div className={`rounded-xl border border-white/10 bg-white/[0.07] p-2.5 backdrop-blur-sm lg:rounded-2xl lg:p-3 ${className}`}>
        <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 bg-white/10 lg:h-8 lg:w-8 lg:rounded-xl">
            <Icon className="h-4 w-4 text-white/85" strokeWidth={1.7} />
        </div>
        <p className="mt-2 text-xs font-semibold text-white lg:text-sm">{title}</p>
        <p className="mt-1 text-[10px] leading-4 text-white/55 lg:text-xs lg:leading-4">{text}</p>
    </div>
);

const DobInput = ({ parts, onChange, visible, onToggleVisibility, invalid }) => {
    const monthRef = useRef(null);
    const yearRef = useRef(null);

    const update = (part, maximumLength, nextRef) => (event) => {
        const value = event.target.value.replace(/\D/g, "").slice(0, maximumLength);
        onChange({ ...parts, [part]: value });
        if (value.length === maximumLength) nextRef?.current?.focus();
    };

    const pasteDate = (event) => {
        const formatted = formatDobInput(event.clipboardData.getData("text"));
        const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(formatted);
        if (!match) return;
        event.preventDefault();
        onChange({ day: match[1], month: match[2], year: match[3] });
        yearRef.current?.focus();
    };

    const inputClass =
        "h-9 min-w-0 border-0 bg-transparent px-0 text-center text-sm shadow-none focus-visible:ring-0";

    return (
        <div
            className="relative mt-2 flex h-11 items-center rounded-xl border border-input bg-background pl-11 pr-12 shadow-sm transition focus-within:border-primary/50 focus-within:ring-4 focus-within:ring-primary/10"
            aria-invalid={invalid}
        >
            <CalendarDays className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
                id="dateOfBirth"
                name="dateOfBirthDay"
                type={visible ? "text" : "password"}
                inputMode="numeric"
                autoComplete="off"
                maxLength={2}
                value={parts.day}
                onChange={update("day", 2, monthRef)}
                onPaste={pasteDate}
                placeholder="DD"
                aria-label="Date of Birth day"
                className={`${inputClass} w-7`}
            />
            <span className="px-1 text-sm font-medium text-muted-foreground" aria-hidden="true">-</span>
            <Input
                ref={monthRef}
                name="dateOfBirthMonth"
                type={visible ? "text" : "password"}
                inputMode="numeric"
                autoComplete="off"
                maxLength={2}
                value={parts.month}
                onChange={update("month", 2, yearRef)}
                placeholder="MM"
                aria-label="Date of Birth month"
                className={`${inputClass} w-7`}
            />
            <span className="px-1 text-sm font-medium text-muted-foreground" aria-hidden="true">-</span>
            <Input
                ref={yearRef}
                name="dateOfBirthYear"
                type={visible ? "text" : "password"}
                inputMode="numeric"
                autoComplete="off"
                maxLength={4}
                value={parts.year}
                onChange={update("year", 4)}
                placeholder="YYYY"
                aria-label="Date of Birth year"
                className={`${inputClass} w-12`}
            />
            <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onToggleVisibility}
                className="absolute right-3 top-1/2 h-8 w-8 -translate-y-1/2 rounded-lg text-muted-foreground hover:text-foreground"
                aria-label={visible ? "Hide Date of Birth" : "Show Date of Birth"}
                title={visible ? "Hide Date of Birth" : "Show Date of Birth"}
            >
                {visible ? <EyeOff /> : <Eye />}
            </Button>
        </div>
    );
};
